import { useTranslation } from "react-i18next";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";
import { daysUntil } from "@/lib/format";

export type LicenseSourceFilter = "all" | "application" | "redeem";

interface LicenseStatsProps {
  licenses: License[];
  source: LicenseSourceFilter;
  onSelectSource?: (source: LicenseSourceFilter) => void;
}

// 顶部统计卡：许可证总数 / 有效 / 到期 / 席位随「来源」筛选联动；
// 另有一张「激活码核销占比」卡专门统计自助核销来的证占比，点一下可直接筛出核销来的许可证。
export default function LicenseStats({ licenses, source, onSelectSource }: LicenseStatsProps) {
  const { t } = useTranslation();
  const scoped = source === "all" ? licenses : licenses.filter((item) => (item.source ?? "application") === source);

  const total = scoped.length;
  const active = scoped.filter((item) => item.status === "active").length;
  const expiringSoon = scoped.filter((item) => {
    if (item.status !== "active" || !item.expires_at) return false;
    const days = daysUntil(item.expires_at);
    return days >= 0 && days <= 30;
  }).length;
  const totalSeats = scoped.reduce((sum, item) => sum + (item.seat_count ?? 0), 0);

  const redeemedTotal = licenses.filter((item) => (item.source ?? "application") === "redeem").length;
  const redeemedPct = licenses.length > 0 ? Math.round((redeemedTotal / licenses.length) * 100) : 0;
  const applicationTotal = licenses.length - redeemedTotal;

  const cards: {
    label: string;
    value: string;
    icon: string;
    tone: string;
    hint: string;
    onClick?: () => void;
    active?: boolean;
  }[] = [
    {
      label: t("licenses.statTotal"),
      value: String(total),
      icon: "ri-key-2-line",
      tone: "primary",
      hint: t("licenses.statTotalHint"),
    },
    { label: t("licenses.statActive"), value: String(active), icon: "ri-shield-check-line", tone: "accent", hint: "" },
    {
      label: t("licenses.statExpiring"),
      value: String(expiringSoon),
      icon: "ri-alarm-warning-line",
      tone: "accent",
      hint: "",
    },
    { label: t("licenses.statSeats"), value: String(totalSeats), icon: "ri-computer-line", tone: "secondary", hint: "" },
    {
      label: t("licenses.statRedeemPct"),
      value: `${redeemedPct}%`,
      icon: "ri-gift-2-line",
      tone: "accent",
      hint: t("licenses.statRedeemHint", { redeem: redeemedTotal, application: applicationTotal }),
      onClick: () => onSelectSource?.(source === "redeem" ? "all" : "redeem"),
      active: source === "redeem",
    },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
      {cards.map((card) => {
        const inner = (
          <>
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-600">{card.label}</span>
              <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                <i className={`${card.icon} text-base`} />
              </span>
            </div>
            <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">{card.value}</p>
            {card.hint && <p className="mt-1 text-[11px] text-foreground-500">{card.hint}</p>}
          </>
        );

        if (!card.onClick) {
          return (
            <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
              {inner}
            </div>
          );
        }

        return (
          <button
            key={card.label}
            type="button"
            onClick={card.onClick}
            title={t("licenses.statRedeemCta")}
            aria-pressed={card.active}
            className={`cursor-pointer rounded-lg border p-4 text-left transition-colors ${
              card.active
                ? "border-accent-400 bg-accent-50"
                : "border-background-200 bg-background-50 hover:border-accent-300 hover:bg-accent-50/60"
            }`}
          >
            {inner}
            <span className="mt-2 flex items-center gap-1 text-[11px] font-medium text-accent-700">
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-filter-3-line" />
              </span>
              {card.active ? t("licenses.statRedeemActive") : t("licenses.statRedeemCta")}
            </span>
          </button>
        );
      })}
    </div>
  );
}